<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import Multiselect from 'vue-multiselect';
import CategoryGroupService from '@/services/category-group';
import ProductService from '@/services/product';
import { productDescription } from '@/utils/product';

export default {
  page: {
    title: 'Grupo de Categorias',
  },
  components: { Layout, PageHeader, Multiselect },
  data() {
    return {
      title: 'Grupo de Categorias',
      group: {
        description: '',
        sequence: null,
        show_categories: false,
        show_as_carousel: false,
        categories: [],
        products: [],
      },
      groupId: '',
      categories: [],
      products: [],
      isLoadingProducts: false,
    };
  },
  async mounted() {
    const loader = this.$loading.show();

    try {
      this.groupId = this.$route.params.id;

      const categoriesResult = await ProductService.getCategories();
      this.categories = categoriesResult.data.list || [];

      if (this.groupId) {
        this.title = 'Editando Grupo de Categorias';

        const groupResult = await CategoryGroupService.get(this.groupId);
        this.group = groupResult.data;

        if (this.group.categories && this.group.categories.length) {
          this.group.categories = this.categories.filter(c => {
            return this.group.categories.some(gc => {
              const id = gc._id || gc;
              return String(id) === String(c._id);
            });
          });
        }

        // Os produtos vem populados do backend, mas sem o fullDescription que o
        // multiselect usa como label (montado apenas na listagem de produtos).
        this.group.products = (this.group.products || []).map(p => this.formatProduct(p));
        this.products = [...this.group.products];
      } else {
        this.title = 'Novo Grupo de Categorias';
      }

      loader.hide();
    } catch (err) {
      this.$toast.error('Ocorreu um erro ao carregar os dados');
      loader.hide();
    }
  },
  methods: {
    formatProduct(product) {
      // Inclui a variação e a cor, do mesmo jeito que o app exibe o produto.
      const description = productDescription(product);

      // O código (SKU ou código interno) entra no label porque só a descrição
      // não identifica alguns produtos na hora de selecionar.
      const code = product.sku || product.code;

      return {
        ...product,
        fullDescription: code ? `${code} - ${description}` : description,
      };
    },
    findProducts(term) {
      this.isLoadingProducts = true;

      ProductService.getAllProducts(1, term).then(res => {
        this.isLoadingProducts = false;

        this.products = (res.data.data || []).map(p => this.formatProduct(p));
      }).catch(() => {
        this.isLoadingProducts = false;
      })
    },
    save() {
      if (!this.group.description) {
        return this.$toast.info('Informe a descrição do grupo');
      }

      const hasCategories = this.group.categories && this.group.categories.length > 0;
      const hasProducts = this.group.products && this.group.products.length > 0;

      if (!hasCategories && !hasProducts) {
        return this.$toast.info('Selecione ao menos uma categoria ou um produto');
      }

      const loader = this.$loading.show();

      const payload = {
        ...this.group,
        categories: (this.group.categories || []).map(c => c._id || c),
        products: (this.group.products || []).map(p => p._id || p),
      };

      CategoryGroupService.save(payload).then(() => {
        this.$toast.open('Dados salvos');
        this.$router.push('/category-groups');
        loader.hide();
      }).catch(() => {
        loader.hide();
        this.$toast.error('Não foi possível salvar o grupo');
      });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title"/>

    <div class="row">
      <div class="col-lg-6">
        <div class="card">
          <div class="card-body">
            <form>
              <div class="row">
                <div class="col-9">
                  <div class="form-group">
                    <label for="groupDescription">Descrição</label>
                    <input id="groupDescription" type="text" class="form-control" v-model="group.description" />
                  </div>
                </div>

                <div class="col-3">
                  <div class="form-group">
                    <label for="groupSequence">Ordem</label>
                    <input id="groupSequence" type="number" class="form-control" v-model="group.sequence" />
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-12">
                  <div class="form-group">
                    <label class="control-label">Categorias do Grupo</label>
                    <multiselect
                      v-model="group.categories"
                      :options="categories"
                      :multiple="true"
                      :close-on-select="false"
                      placeholder="Selecione as categorias"
                      label="full_description"
                      track-by="_id"
                      selectLabel="Enter para selecionar"
                      selectedLabel="Selecionada"
                      deselectLabel="Enter para remover"
                    ></multiselect>
                    <small class="text-muted">Selecione uma ou mais categorias que farão parte deste grupo.</small>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-12">
                  <div class="form-group">
                    <label class="control-label">Produtos do Grupo (opcional)</label>
                    <multiselect
                      v-model="group.products"
                      :options="products"
                      :multiple="true"
                      :close-on-select="false"
                      :clear-on-select="true"
                      :loading="isLoadingProducts"
                      @search-change="findProducts"
                      placeholder="Digite para buscar os produtos"
                      label="fullDescription"
                      track-by="_id"
                      selectLabel="Enter para selecionar"
                      selectedLabel="Selecionado"
                      deselectLabel="Enter para remover"
                    ></multiselect>
                    <small class="text-muted">
                      Produtos específicos que farão parte deste grupo, além dos produtos das
                      categorias selecionadas acima. Deixe em branco para usar apenas as categorias.
                    </small>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-12">
                  <div class="form-group">
                    <div class="custom-control custom-checkbox">
                      <input
                        id="groupShowCategories"
                        type="checkbox"
                        class="custom-control-input"
                        v-model="group.show_categories"
                      />
                      <label class="custom-control-label" for="groupShowCategories">
                        Exibir lista de categorias
                      </label>
                    </div>
                    <small class="text-muted">
                      Quando marcado, o grupo aparece primeiro na lista do app e exibe as categorias como submenu.
                      Caso contrário, apenas o nome do grupo é exibido (sem submenu).
                    </small>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-12">
                  <div class="form-group">
                    <div class="custom-control custom-checkbox">
                      <input
                        id="groupShowAsCarousel"
                        type="checkbox"
                        class="custom-control-input"
                        v-model="group.show_as_carousel"
                      />
                      <label class="custom-control-label" for="groupShowAsCarousel">
                        Exibir como carrossel na tela inicial
                      </label>
                    </div>
                    <small class="text-muted">
                      Quando marcado, os produtos das categorias e os produtos específicos deste
                      grupo são exibidos como um carrossel na tela inicial da loja.
                    </small>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <div class="row mt-4 align-items-center">
      <div class="col-sm-6">
        <router-link
          tag="a"
          to="/category-groups"
          class="btn text-muted d-none d-sm-inline-block btn-link"
        >
          <i class="mdi mdi-arrow-left mr-1"></i> Voltar para lista de grupos
        </router-link>
      </div>
      <div class="col-sm-6">
        <div class="text-sm-right">
          <button type="button" class="btn btn-primary btn-rounded mb-2 mr-2" @click="save">
            <i class="mdi mdi-content-save mr-1"></i> Salvar Grupo
          </button>
        </div>
      </div>
    </div>
  </Layout>
</template>
